import React from 'react';
import type {
	Extension,
	ExtensionData,
	ForgeRequestCreatePropertyFormData,
	ForgeAppRequestCreatePropertyData,
	WithExtensionListProps,
	WithBlockedAppsBannerProps,
} from '@helpcenter/forge-ui-utils';
import {
	FORGE_REQUEST_CREATE,
	PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE,
	TOP_LEVEL_COMPONENTS,
} from '@helpcenter/forge-ui-utils/src/constants';
import { ExtensionPointProvider } from '@helpcenter/forge-ui-utils/src/extension-point-provider';
import { ExtensionProvider } from '@helpcenter/forge-ui-utils/src/extension-provider';
import { Renderer } from '@helpcenter/forge-ui-utils/src/renderer';
import { useShowBanner } from 'utils/blocked-apps-banner';
import { TopLevelComponent } from 'view/common/forge-ui/top-level-component';
import { withExtensionList } from 'view/common/forge-ui/with-extension-list';
import { requestCreatePropertyPanelUIKitComponents } from 'view/common/forge-ui-components';
import type { ComponentMap } from '@atlassian/forge-ui';

export interface RequestCreatePropertyPanelModuleProps extends WithExtensionListProps {
	portalId: number;
	requestTypeId: number;
	onUpdateRequestPropertyData: (data: ForgeRequestCreatePropertyFormData) => void;
}

export interface ForgeRequestCreateProps {
	extension: Extension;
	extensionData: ExtensionData;
	onUpdate: (data: ForgeRequestCreatePropertyFormData) => void;
}

export const ForgeRequestCreatePropertyReactFragment: React.FC<ForgeRequestCreateProps> = ({
	extension,
	extensionData,
	onUpdate,
}) => {
	const submit = (payload: ForgeAppRequestCreatePropertyData) => {
		const extId = extension.id.split('/');
		onUpdate({ data: payload.data, extensionId: extId[1] });
		return true;
	};

	const getComponents = React.useCallback(
		(defaults: ComponentMap): ComponentMap => ({
			...defaults,
			...requestCreatePropertyPanelUIKitComponents,
			[TOP_LEVEL_COMPONENTS[PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE]]: TopLevelComponent,
		}),
		[],
	);

	return (
		<React.Fragment key={extension.id}>
			<ExtensionProvider extension={extension}>
				<Renderer
					extension={extension}
					extensionData={extensionData}
					getComponents={getComponents}
					bridge={{ submit }}
				/>
			</ExtensionProvider>
		</React.Fragment>
	);
};

export const RequestCreatePropertyPanelModuleDumb: React.FC<
	RequestCreatePropertyPanelModuleProps & WithBlockedAppsBannerProps
> = ({
	extensions,
	portalId,
	requestTypeId,
	onUpdateRequestPropertyData,
	showBanner,
	resetBannerState,
}) => {
	const extensionData: ExtensionData = React.useMemo(
		() => ({
			type: PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE,
			portal: { id: portalId },
			request: { typeId: requestTypeId },
		}),
		[portalId, requestTypeId],
	);

	const allowedExtensions = useShowBanner(
		extensions,
		FORGE_REQUEST_CREATE,
		true,
		showBanner,
		resetBannerState,
	);

	return (
		<>
			{allowedExtensions.length > 0 && (
				<ExtensionPointProvider extensionType={PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE}>
					{allowedExtensions.map(
						(extension: Extension) =>
							extension && (
								<ForgeRequestCreatePropertyReactFragment
									extension={extension}
									extensionData={extensionData}
									onUpdate={onUpdateRequestPropertyData}
									key={extension.id}
								/>
							),
					)}
				</ExtensionPointProvider>
			)}
		</>
	);
};

export const RequestCreatePropertyPanel = withExtensionList(
	PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE,
)(RequestCreatePropertyPanelModuleDumb);

export default RequestCreatePropertyPanel;
